import { useQuery } from "@tanstack/react-query";
import axios from "axios";

import {
  IAvailableRoom,
  IAvailableRoomsSearch,
  IBookableService,
  IFee,
  IGetMobilePaymentByIdResponse,
  IHotelStoreDetails,
  IProductOnline,
  IPublicProvider,
  IPublicSlot,
  OrderStatusScanOrderResponse,
  ProductCategory,
  ScanToOrderTableResponse,
  StoreDetailsPublicResponse,
} from "../types/types";
import { EXHUT_DOH_SCAN_ORDER_AUTH_CODE } from "../utils/constants";

export function useGetMobilePaymentById(storeId?: string, paymentId?: string) {
  return _useGet<IGetMobilePaymentByIdResponse>(
    `public/stores/${storeId}/mobile-payments/${paymentId}`,
    ["public", "stores", storeId, "mobile-payments", `${paymentId}`],
    !!storeId,
  );
}

export function useGetAddressSuggestions(
  scanToOrderExternalStoreId?: string,
  address?: string,
) {
  return _useGet<string[]>(
    `public/stores/${scanToOrderExternalStoreId}/online-order/address-suggestions?address=${address}`,
    [
      "public",
      "stores",
      scanToOrderExternalStoreId,
      "online-order",
      "address-suggestions",
      address,
    ],
    !!address,
  );
}

export function useGetRestaurantTableItemStatuses(
  scanToOrderExternalStoreId?: string,
  tableId?: string,
) {
  return _useGet<OrderStatusScanOrderResponse[]>(
    `public/stores/${scanToOrderExternalStoreId}/scan-to-order/${tableId}/status`,
    [
      "public",
      "stores",
      scanToOrderExternalStoreId,
      "scan-to-order",
      tableId,
      "status",
    ],
    !!tableId,
    {
      "x-scan-to-order-auth-code": localStorage.getItem(
        EXHUT_DOH_SCAN_ORDER_AUTH_CODE,
      ),
    },
  );
}

export function useGetRestaurantTableDetails(
  scanToOrderExternalStoreId?: string,
  tableId?: string | null,
) {
  return _useGet<ScanToOrderTableResponse>(
    `public/stores/${scanToOrderExternalStoreId}/scan-to-order/${tableId}/table-details`,
    [
      "public",
      "stores",
      scanToOrderExternalStoreId,
      "scan-to-order",
      tableId,
      "table-details",
    ],
    !!tableId,
    {
      "x-scan-to-order-auth-code": localStorage.getItem(
        EXHUT_DOH_SCAN_ORDER_AUTH_CODE,
      ),
    },
  );
}

// ---- Public appointment self-booking ----

export function useGetBookableServices(storeId?: string) {
  return _useGet<IBookableService[]>(
    `public/stores/${storeId}/scheduling/services`,
    ["public", "stores", storeId, "scheduling", "services"],
    !!storeId,
  );
}

export function useGetSchedulingProviders(
  storeId?: string,
  productIds: string[] = [],
) {
  const ids = productIds.join(",");
  return _useGet<IPublicProvider[]>(
    `public/stores/${storeId}/scheduling/providers?productIds=${ids}`,
    ["public", "stores", storeId, "scheduling", "providers", ids],
    !!storeId,
  );
}

export function useGetSchedulingAvailability(
  storeId?: string,
  params?: {
    date?: string;
    productIds?: string[];
    employeeId?: number;
    tzOffsetMinutes?: number;
  },
) {
  const search = new URLSearchParams();
  if (params?.date) search.set("date", params.date);
  if (params?.productIds?.length) {
    search.set("productIds", params.productIds.join(","));
  }
  if (params?.employeeId !== undefined) {
    search.set("employeeId", String(params.employeeId));
  }
  search.set("tzOffsetMinutes", String(params?.tzOffsetMinutes ?? 0));

  return _useGet<IPublicSlot[]>(
    `public/stores/${storeId}/scheduling/availability?${search.toString()}`,
    [
      "public",
      "stores",
      storeId,
      "scheduling",
      "availability",
      params?.date,
      params?.productIds?.join(","),
      params?.employeeId !== undefined ? String(params.employeeId) : "any",
    ],
    !!storeId && !!params?.date && !!params?.productIds?.length,
  );
}

export function useGetStoreDetailsForOnlineOrder(externalStoreId?: string) {
  return _useGet<StoreDetailsPublicResponse>(
    `public/stores/${externalStoreId}/online-orders/details`,
    ["public", "stores", externalStoreId, "online-orders", "details"],
    !!externalStoreId,
  );
}

export function useGetOnlineFees(storeId?: string) {
  return _useGet<IFee[]>(
    `public/stores/${storeId}/online-orders/fees`,
    ["public", "stores", storeId, "online-orders", `fees`],
    !!storeId,
  );
}

export function useGetProductCategoriesForOnlineOrder(
  scanToOrderExternalStoreId?: string,
  name?: string,
) {
  return _useGet<ProductCategory[]>(
    `public/stores/${scanToOrderExternalStoreId}/online-orders/products-categories`,
    [
      scanToOrderExternalStoreId,
      "products-categories",
      scanToOrderExternalStoreId,
    ],
    !!scanToOrderExternalStoreId,
  );
}

export function useGetProductCategories(
  scanToOrderExternalStoreId?: string,
  name?: string,
) {
  return _useGet<ProductCategory[]>(
    `public/stores/${scanToOrderExternalStoreId}/scan-to-order/products-categories`,
    [
      scanToOrderExternalStoreId,
      "products-categories",
      scanToOrderExternalStoreId,
    ],
    !!scanToOrderExternalStoreId,
  );
}

export function useGetProductsForOnlineOrder(
  scanToOrderExternalStoreId?: string,
  name?: string,
) {
  return _useGet<IProductOnline[]>(
    `public/stores/${scanToOrderExternalStoreId}/online-orders/products?search=${name}&includeCategory=true`,
    [scanToOrderExternalStoreId, "online-orders", "products", name],
    !!scanToOrderExternalStoreId,
  );
}

export function useGetProducts(
  scanToOrderExternalStoreId?: string,
  name?: string,
) {
  return _useGet<IProductOnline[]>(
    `public/stores/${scanToOrderExternalStoreId}/products?search=${name}&includeCategory=true`,
    [scanToOrderExternalStoreId, "products", name],
    !!scanToOrderExternalStoreId,
  );
}

export function useGetProductsForScanToOrder(
  scanToOrderExternalStoreId?: string,
  name?: string,
) {
  return _useGet<IProductOnline[]>(
    `public/stores/${scanToOrderExternalStoreId}/scan-to-order/products?search=${name}&includeCategory=true`,
    [scanToOrderExternalStoreId, "scan-to-order", "products", name],
    !!scanToOrderExternalStoreId,
  );
}

export function useGetFeaturedProducts(scanToOrderExternalStoreId?: string) {
  return _useGet<IProductOnline[]>(
    `public/stores/${scanToOrderExternalStoreId}/products-featured`,
    [scanToOrderExternalStoreId, "products-featured"],
    !!scanToOrderExternalStoreId,
  );
}

export function useGetFeaturedProductsForOnline(
  scanToOrderExternalStoreId?: string,
) {
  return _useGet<IProductOnline[]>(
    `public/stores/${scanToOrderExternalStoreId}/online-orders/products-featured`,
    [scanToOrderExternalStoreId, "online-orders", "products-featured"],
    !!scanToOrderExternalStoreId,
  );
}

/**
 * Store details for the hotel booking page. Served by exhut-hms-service (its
 * online-settings + the property info), so it hits the HMS base url.
 */
export function useGetHotelStoreDetails(externalStoreId?: string) {
  return _useGet<IHotelStoreDetails>(
    `public/stores/${externalStoreId}/hotel/details`,
    ["public", "stores", externalStoreId, "hotel", "details"],
    !!externalStoreId,
    undefined,
    process.env.REACT_APP_BE_URL,
  );
}

/**
 * Search bookable hotel rooms for a store. Rooms live in exhut-hms-service, so
 * this hits the HMS base url (not the business-service one) directly. Only
 * fires once both dates are supplied.
 */
export function useSearchAvailableRooms(
  externalStoreId?: string,
  search?: IAvailableRoomsSearch,
) {
  const params = new URLSearchParams();
  if (search?.checkInDate) params.set("checkInDate", search.checkInDate);
  if (search?.checkOutDate) params.set("checkOutDate", search.checkOutDate);
  if (search?.numberOfGuests) {
    params.set("numberOfGuests", String(search.numberOfGuests));
  }

  return _useGet<IAvailableRoom[]>(
    `public/stores/${externalStoreId}/hotel/rooms?${params.toString()}`,
    [
      "public",
      "stores",
      externalStoreId,
      "hotel",
      "rooms",
      search?.checkInDate,
      search?.checkOutDate,
      search?.numberOfGuests ? String(search.numberOfGuests) : undefined,
    ],
    !!externalStoreId && !!search?.checkInDate && !!search?.checkOutDate,
    undefined,
    process.env.REACT_APP_BE_URL,
  );
}

function _useGet<T1>(
  path: string,
  keys: (string | undefined | null)[],
  enabled?: boolean,
  customHeaders?: any,
  hostUrlOverride?: string,
) {
  let headers: any = {};

  const hostUrl = hostUrlOverride ?? process.env.REACT_APP_BE_URL;

  if (customHeaders) {
    headers = {
      ...customHeaders,
    };
  }
  return useQuery<T1, Error>({
    queryFn: async () => {
      return axios
        .get(`${hostUrl}/${path}`, {
          headers,
        })
        .then((resp) => resp.data);
    },
    queryKey: keys,
    enabled,
    retry: 0,
  });
}
